/**
 * Extracts all form field values from prompt modules and tools that might contain template variables.
 *
 * Extracted form fields:
 * - Prompt module instructions (the content of the prompt module)
 * - Tool URLs
 * - Tool header values
 * - Tool parameter values (including nested parameters up to 10 levels deep)
 * - Tool parameter array item values
 *
 * Returns an array of text values to be scanned for {{.variableName}} patterns.
 */
export function extractTextForVariableDetection(promptModules: any[], tools: any[]): (string | null | undefined)[] {
  const textSources: (string | null | undefined)[] = [];

  // Add prompt module instructions
  textSources.push(...extractPromptModuleTexts(promptModules));

  // Add tool URLs, headers, and parameters
  textSources.push(...extractAllToolsTexts(tools));

  return textSources;
}

// Prompt Modules
// ----------------------------------------

/**
 * Extracts instruction text from prompt modules.
 * These may contain template variables to expose in the configuration UI.
 */
function extractPromptModuleTexts(promptModules: any[]): (string | null | undefined)[] {
  return promptModules.map((promptModule) => promptModule.controls.instructions.getRawValue());
}

// Tools
// ----------------------------------------

/**
 * Extracts all text from an array of tools.
 * These may contain template variables to expose in the configuration UI.
 */
function extractAllToolsTexts(tools: any[]): (string | null | undefined)[] {
  const textSources: (string | null | undefined)[] = [];
  tools.forEach((tool) => {
    textSources.push(...extractToolTexts(tool));
  });
  return textSources;
}

/**
 * Extracts all text from a single tool control (URL, headers, parameters).
 * These may contain template variables to expose in the configuration UI.
 */
function extractToolTexts(toolControl: any): (string | null | undefined)[] {
  const textSources: (string | null | undefined)[] = [];

  // Add tool URL
  textSources.push(toolControl.controls.url.getRawValue());

  // Add tool headers
  textSources.push(...extractToolHeaderTexts(toolControl.controls.headers.controls));

  // Add tool parameters (recursively)
  textSources.push(...extractParameterTexts(toolControl.controls.parameters, new Set()));

  return textSources;
}

// Tool Headers
// ----------------------------------------

/**
 * Extracts text from tool headers values
 * These may contain template variables to expose in the configuration UI.
 */
function extractToolHeaderTexts(headers: any[]): (string | null | undefined)[] {
  const textSources: (string | null | undefined)[] = [];
  headers.forEach((header) => {
    textSources.push(header.controls.value.getRawValue());
  });
  return textSources;
}

// Tool Parameters
// ----------------------------------------

const MAX_RECURSION_DEPTH = 10;

/**
 * Recursively extracts text from parameter controls at any depth.
 * Only extracts from 'value' fields which may contain template variables.
 * Limited to 10 levels of recursion to prevent infinite loops.
 */
function extractParameterTexts(
  parametersControl: any,
  visitedControls: Set<any> = new Set(),
  depth = 0,
): (string | null | undefined)[] {
  const textSources: (string | null | undefined)[] = [];

  if (!parametersControl?.controls || visitedControls.has(parametersControl) || depth >= MAX_RECURSION_DEPTH) {
    return textSources;
  }

  visitedControls.add(parametersControl);

  parametersControl.controls.forEach((parameterControl: any) => {
    textSources.push(...extractSingleParameterTexts(parameterControl, visitedControls, depth));
  });

  return textSources;
}

/**
 * Extracts text from a single parameter and its nested structure.
 * Looks for template variables in parameter value fields.
 * Limited to 10 levels of recursion to prevent infinite loops.
 */
function extractSingleParameterTexts(
  parameterControl: any,
  visitedControls: Set<any> = new Set(),
  depth = 0,
): (string | null | undefined)[] {
  const textSources: (string | null | undefined)[] = [];

  if (visitedControls.has(parameterControl) || depth >= MAX_RECURSION_DEPTH) {
    return textSources;
  }

  visitedControls.add(parameterControl);

  // Extract text from the value field at this level
  if (parameterControl.controls?.value) {
    const fieldValue = parameterControl.controls.value.getRawValue();
    textSources.push(fieldValue);
  }

  // Recursively handle nested parameters (FormArray)
  if (parameterControl.controls?.nestedParameters?.controls) {
    parameterControl.controls.nestedParameters.controls.forEach((nestedParam: any) => {
      textSources.push(...extractSingleParameterTexts(nestedParam, new Set(visitedControls), depth + 1));
    });
  }

  // Recursively handle array item parameter (single FormGroup)
  if (parameterControl.controls?.items) {
    const itemsControl = parameterControl.controls.items;

    // Extract text from the items parameter value field
    if (itemsControl.controls?.value) {
      const fieldValue = itemsControl.controls.value.getRawValue();
      textSources.push(fieldValue);
    }

    // Handle nested parameters within items
    if (itemsControl.controls?.nestedParameters?.controls) {
      itemsControl.controls.nestedParameters.controls.forEach((nestedParam: any) => {
        textSources.push(...extractSingleParameterTexts(nestedParam, new Set(visitedControls), depth + 1));
      });
    }
  }

  return textSources;
}
